import { ReactNode } from 'react'
import { useTitle } from './TitleProvider'

interface Props {
    title: ReactNode
    subtitle?: string
    actions?: ReactNode
    /**
     * Parent-page link shown above the title — use MastheadBreadcrumb. Sub-pages
     * used to scatter their own "Back to settings" links through the body; this
     * gives them one consistent home.
     */
    breadcrumb?: ReactNode
    /**
     * Badge sat beside the title — use LevelPill — naming what kind of entity the
     * page describes on pages that look alike (org / team / profile).
     */
    levelPill?: ReactNode
    /**
     * Sets the browser-tab title. Pass this on pages migrated off TextPageHeader,
     * which used to own the tab title via useTitle — the visual title prop can be
     * a ReactNode, so this string keeps the tab text correct and clean.
     */
    tabTitle?: string
}

// useTitle is a hook, so it can't be called conditionally. Mounting this child
// only when tabTitle is set keeps the tab title opt-in without breaking the
// rules of hooks or clobbering titles on pages that set their own.
function TabTitle({ title }: { title: string }) {
    useTitle(title)
    return null
}

/**
 * Shared page masthead: white header strip, large Jost title with the arwen dot,
 * muted subtitle, and an actions slot on the right. Uses the ink scale so every
 * page header reads as one visual language.
 */
export default function PageMasthead({
    title,
    subtitle,
    actions,
    tabTitle,
    breadcrumb,
    levelPill
}: Props) {
    return (
        <>
            {tabTitle && <TabTitle title={tabTitle} />}
            <header
            className={`border-b border-ink-200 bg-white px-4 sm:px-6 lg:px-8 ${
                // With a subtitle: the standard top/bottom padding. Without one, the
                // subtitle's vertical space (pt-4 + gap + line + pb-5 = 62px) is split
                // evenly top and bottom (31px = 1.9375rem) instead — so the masthead
                // keeps the same height as the home page (which has a subtitle) while
                // the lone title sits vertically centred rather than pinned to the top.
                // A breadcrumb fills that space itself, so it takes the standard padding.
                subtitle || breadcrumb ? 'pb-5 pt-4' : 'py-[1.9375rem]'
            }`}
        >
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="min-w-0">
                    {/* Sizing lives here so a bare trail of links reads correctly;
                        MastheadBreadcrumb sets its own type and wins where used. */}
                    {breadcrumb && <div className="mb-1.5 text-xs text-ink-600">{breadcrumb}</div>}
                    {/* Row so an optional levelPill can sit beside the title; with no
                        pill it holds the h1 alone and the gap never shows. */}
                    <div className="flex flex-wrap items-center gap-3">
                        {/* Explicit Jost family + !font-medium override the global
                            Merriweather h1-h4 rule in index.css — app headings are Jost 500.
                            font-sans can't be used here: tailwind.config.js doesn't map
                            fontFamily.sans, so font-sans resolves to the system stack, not Jost. */}
                        <h1 className="font-['Jost',system-ui,sans-serif] !font-medium text-[clamp(1.5rem,1.1rem+1.4vw,2.125rem)] leading-[1.1] tracking-tight text-ink-900">
                            {title}
                            <span aria-hidden="true" className="text-arwen-600">
                                .
                            </span>
                        </h1>
                        {levelPill}
                    </div>
                    {subtitle && <p className="mt-1.5 text-sm text-ink-500">{subtitle}</p>}
                </div>
                {actions && <div className="flex flex-none items-center gap-3 pb-1">{actions}</div>}
            </div>
            </header>
        </>
    )
}
